/** Angular imports */
import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    inject,
    OnDestroy,
    PLATFORM_ID,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, map, of } from 'rxjs';

/** Core imports */
import { DealerService, ExportService } from '@core';

/** Component imports */
import {
    Button,
    Card,
    DropdownOption,
    getDynamicTimeFrames,
    GraphComponent,
    GraphConfig,
    GraphVariant,
    Skeleton,
} from '@ntv360/component-pantry';

/** Local imports */
import {
    ApiDealerInformation,
    ApiDealerStats,
    ApiDealerTableRow,
} from '@core/services/dealer/dealer.interface';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import {
    ConfirmationModalComponent,
    FilterDropdownComponent,
    StatsCardComponent,
    TableColumn,
    TableComponent,
    TableToolbarButton,
    TableToolbarComponent,
} from '@shared';
import type { FilterOption } from '@shared/components/filter-dropdown/filter-dropdown.component';
import { SVG_ICONS } from '@shared/constants';
import { StatsData } from '@shared/interfaces/stats.interface';
import { getExportData, searchHandler, withLoading } from '@shared/utils';
import {
    DEALER_COLUMNS,
    DEALER_FILTER_OPTIONS,
    DEALER_GROUPED_COLUMNS,
    DEALER_PAGE_SIZE,
    DEALER_STATUS_FILTER_CONFIG,
    DEALERS_TABLE_COLUMNS,
} from './constants';
import {
    DEALER_SETUP_DATA,
    DEALERS_ANALYTICS_CHART_DATA,
    RISK_STATUS_DATA,
} from './constants/dummyData.constants';

@Component({
    selector: 'app-dealers',
    standalone: true,
    imports: [
        StatsCardComponent,
        Button,
        FilterDropdownComponent,
        ConfirmationModalComponent,
        Card,
        GraphComponent,
        Skeleton,
        TableToolbarComponent,
        TableComponent,
    ],
    templateUrl: './dealers.component.html',
    styleUrl: './dealers.component.scss',
})
export class DealersComponent implements AfterViewInit, OnDestroy {
    /**
     * Platform identifier used to avoid browser-only components during SSR.
     */
    private platformId = inject(PLATFORM_ID);

    /**
     * Destroy ref used to clean up the dealer data subscription.
     */
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Export service used to generate Excel files from the dealer table data.
     */
    private readonly exportService = inject(ExportService);

    /**
     * Dealer service used to fetch dealer dashboard stats.
     */
    private readonly dealerService = inject(DealerService);

    /** In-app navigation to single-dealer (`/dealers/:id`). */
    private readonly router = inject(Router);

    /**
     * Sub-Navbar service used to project the dealer actions into the shared layout.
     */
    protected subNavbarService = inject(SubNavbarService);

    /**
     * Content reference to project into the sub-navbar component.
     */
    protected subNavBarContent = viewChild<TemplateRef<void>>('subNavBarContent');

    /**
     * True only in the browser so SSR does not instantiate the graph component.
     */
    public readonly isBrowser = isPlatformBrowser(this.platformId);

    /** Dealer table columns */
    protected dealerTableColumns = DEALERS_TABLE_COLUMNS;

    /** Special property for techmagic family */
    protected showForTM = signal<boolean>(false);

    /**
     * Current year shown in the analytics subtitle.
     */
    public readonly currentYear = new Date().getFullYear();

    /**
     * Data for the dealers table.
     */
    protected dealerData = signal<ApiDealerTableRow[]>([]);

    /**
     * Dealer dashboard stats returned by the API.
     */
    private dealerStats = signal<ApiDealerStats | null>(null);

    /**
     * Total dealer count from the API pagination metadata.
     */
    public readonly dealerTotalCount = signal<number>(0);

    /**
     * Current page for pagination (starting at 1).
     */
    protected currentPage = signal<number>(1);

    /**
     * Whether a load-more request is in progress.
     */
    protected loadingMore = signal<boolean>(false);
    protected isDeletingDealer = signal<boolean>(false);

    /**
     * Single loading source for the initial dealers dashboard data.
     */
    protected readonly isPageLoading = signal<boolean>(true);

    /**
     * To store current filter selections
     */
    protected selectedFilters = signal<FilterOption[]>([]);

    /**
     * Search term entered in the toolbar searchbar.
     */
    protected searchTerm = signal<string>('');

    /**
     * Single source of truth for the analytics skeleton state.
     */
    public readonly shouldShowAnalyticsSkeleton = computed(
        () => !this.isBrowser || this.isPageLoading(),
    );

    /**
     * Static dealer analytics chart data used for the dummy analytics panel.
     */
    private readonly chartData = signal(DEALERS_ANALYTICS_CHART_DATA);

    /** Dummy configuration for dealer setup progress graph */
    public readonly dealerSetupData = signal<GraphConfig>({
        graphData: DEALER_SETUP_DATA,
        variant: GraphVariant.LineWithFilterLegend,
        timeFrames: getDynamicTimeFrames(),
        header: 'Dealer Setup Progress Overview',
        size: 'medium',
        colors: ['#095AF3', '#A9DA5C', '#F6284E'],
    });

    /** Dummy configuration for risk status overview graph */
    public readonly riskStatusData = signal<GraphConfig>({
        graphData: RISK_STATUS_DATA,
        variant: GraphVariant.BarWithFilterLegend,
        timeFrames: getDynamicTimeFrames(),
        header: 'Risk Status Overview',
        size: 'medium',
        colors: ['#A9DA5C', '#FFAE00', '#F6284E'],
    });

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Page size for paginated requests.
     */
    protected readonly pageSize = DEALER_PAGE_SIZE;

    /**
     * List of stats cards to display.
     */
    public statsData = computed<StatsData[]>(() => {
        const stats = this.dealerStats();

        if (!stats) return [];

        return [
            {
                title: 'Total Dealer(s)',
                description: 'Live dealer totals from the API.',
                iconKey: 'dealers',
                total: String(stats.totalDealers.overall),
            },
            {
                title: 'New Dealer Registrations',
                description: 'This week compared with the rest of the period.',
                iconKey: 'dealers',
                total: String(stats.totalDealers.thisWeek),
            },
            {
                title: 'Last week',
                description: 'Last week dealer registrations.',
                iconKey: 'dealers',
                total: String(stats.totalDealers.lastWeek),
            },
        ];
    });

    /**
     * Column definitions for the dealer table (flat structure).
     */
    public dealerColumns = signal<TableColumn[]>(DEALER_COLUMNS);

    /**
     * Column definitions for the dealer table (grouped header structure).
     */
    public dealerGroupedColumns = signal<TableColumn[]>(DEALER_GROUPED_COLUMNS);

    /**
     * Dropdown config for status filter (UI-only for now).
     */
    public statusConfig = DEALER_STATUS_FILTER_CONFIG;

    /**
     * Analytics filter dropdowns shown in the sub-navbar panel.
     */
    public readonly graphFilterConfigs = computed(() => [
        {
            placeholder: 'Month',
            options: this.chartData().categories.map((label, index) => ({ label, value: index })),
            handler: (opt: DropdownOption | null) => this.onMonthChange(opt),
        },
        {
            placeholder: 'Year',
            options: [
                { label: String(this.currentYear), value: this.currentYear },
                { label: String(this.currentYear - 1), value: this.currentYear - 1 },
                { label: String(this.currentYear - 2), value: this.currentYear - 2 },
                { label: String(this.currentYear - 3), value: this.currentYear - 3 },
            ],
            handler: (opt: DropdownOption | null) => this.onYearChange(opt),
        },
    ]);

    /**
     * Currently selected month filter for the dummy analytics panel.
     */
    protected selectedMonth = signal<number | null>(null);

    /**
     * Currently selected year filter for the dummy analytics panel.
     */
    protected selectedYear = signal<number | null>(null);

    /**
     * Signal to track selected rows in the table.
     */
    public selectedRows = signal<any[]>([]);

    /**
     * Tracks the dealer targeted by a single-row delete action.
     */
    protected selectedDealer = signal<ApiDealerTableRow | null>(null);

    /**
     * Controls visibility of the delete confirmation modal.
     */
    public showDeleteConfirmationModal = signal<boolean>(false);

    /**
     * Controls visibility of the status (success/error) modal.
     */
    public showStatusModal = signal<boolean>(false);

    /**
     * The type of status modal to display.
     */
    public statusModalType = signal<'success' | 'error' | 'warning'>('success');

    /**
     * Title displayed in the status modal.
     */
    public statusModalTitle = signal<string>('');

    /**
     * Message displayed in the status modal.
     */
    public statusModalMessage = signal<string>('');

    /**
     * Confirmation title for the delete modal.
     */
    public deleteConfirmationTitle = computed(() =>
        this.selectedDealer() ? 'Confirm Delete' : 'Confirm Bulk Delete',
    );

    /**
     * Confirmation message for the delete modal.
     */
    public deleteConfirmationMessage = computed(() => {
        const selectedDealer = this.selectedDealer();

        if (selectedDealer) {
            return `Are you sure you want to delete ${selectedDealer.businessName}? This action cannot be undone.`;
        }

        const selectedCount = this.selectedRows().length;

        return `Are you sure you want to delete ${selectedCount} selected item(s)? This action cannot be undone.`;
    });

    /**
     * Signal to track the loading state for the table.
     */
    public tableLoading = signal<boolean>(false);

    /**
     * Signal to track whether analytics controls are visible.
     */
    protected showAnalytics = signal<boolean>(false);

    /**
     * Signal to track the loading state for the table.
     */
    public isLoading = signal<boolean>(false);

    /**
     * Computed list of toolbar buttons, reacting to selection changes.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 2 ? 'danger' : 'secondary',
                disabled: selectionCount <= 2 ? true : false,
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Handles search functionlity, comment later
     */
    constructor() {
        searchHandler(
            this.searchTerm,
            (searchTerm) => {
                return this.dealerService
                    .searchDealers(searchTerm)
                    .pipe(withLoading(this.isLoading));
            },
            this.destroyRef,
            {
                next: (res) => {
                    const mappedData = this.mapDataToUI(res.data);
                    this.dealerData.set(mappedData);
                    this.dealerTotalCount.set(mappedData.length);
                },
                onEmpty: () => {
                    this.loadInitialData();
                },
            },
        );
    }

    /**
     * Handles action click events from the table.
     */
    public onActionClick(event: { action: string; row: ApiDealerTableRow }): void {
        if (event.action.toLowerCase() === 'delete') {
            this.selectedDealer.set(event.row);
            this.showDeleteConfirmationModal.set(true);
            return;
        }
    }

    /**
     * Handles cell click events from the table.
     * Only columns with `hasRedirect` emit `cellClick` (see `DEALERS_TABLE_COLUMNS`).
     */
    public onCellClick(event: { field: string; value: any; row: any }): void {
        const navigableFields = new Set(['dealerAlias', 'businessName']);
        if (!navigableFields.has(event.field)) {
            return;
        }
        const id = event.row?.id;
        if (id == null || id === '') {
            return;
        }
        void this.router.navigate(['/dealers', String(id)]);
    }

    /**
     * Handles selection change events from the table.
     */
    public onSelectionChange(selectedRows: any[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Toggles analytics button state.
     */
    public toggleAnalytics(): void {
        this.showAnalytics.update((value) => !value);
    }

    /**
     * Loads the next page of dealers and appends to existing data.
     * Called when the table's "show more" button is clicked.
     */
    public loadMoreDealers(): void {
        // Prevent concurrent requests
        if (this.loadingMore() || this.tableLoading()) return;

        // Check if there are more pages to load
        const totalCount = this.dealerTotalCount();
        const currentDataLength = this.dealerData().length;
        if (currentDataLength >= totalCount) return;

        this.loadingMore.set(true);
        this.tableLoading.set(true);
        const nextPage = this.currentPage() + 1;

        this.dealerService
            .getDealers({ page: nextPage, limit: this.pageSize })
            .pipe(
                catchError((error) => {
                    console.error('Failed to load more dealers.', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (response) => {
                    // Append new dealers to existing data
                    this.dealerData.update((current) => [
                        ...current,
                        ...this.mapDataToUI(response.data),
                    ]);

                    // Update pagination metadata (already set in initial load, but updated here for consistency)
                    this.dealerTotalCount.set(response.meta.pagination.totalCount);
                    this.currentPage.set(nextPage);
                    this.loadingMore.set(false);
                    this.tableLoading.set(false);
                },
                error: () => {
                    this.loadingMore.set(false);
                    this.tableLoading.set(false);
                },
            });
    }

    /**
     * Collapses the accumulated dealer rows back to the first page.
     * Resets `currentPage` so a subsequent "Show More" re-fetches page 2.
     */
    public onShowLess(): void {
        this.dealerData.update((current) => current.slice(0, this.pageSize));
        this.currentPage.set(1);
    }

    /**
     * Updates the dummy month filter state.
     */
    public onMonthChange(option: DropdownOption | null): void {
        this.selectedMonth.set(
            option?.value === undefined || option?.value === null ? null : Number(option.value),
        );
    }

    /**
     * Updates the dummy year filter state.
     */
    public onYearChange(option: DropdownOption | null): void {
        this.selectedYear.set(
            option?.value === undefined || option?.value === null ? null : Number(option.value),
        );
    }

    /**
     * Handles toolbar action click events.
     */
    public onToolbarAction(action: string): void {
        if (action === 'Bulk Delete') {
            if (this.selectedRows().length <= 2) return;

            this.selectedDealer.set(null);
            this.showDeleteConfirmationModal.set(true);
            return;
        }

        if (action === 'Export') {
            const dataToExport = getExportData(this.selectedRows(), this.dealerData());
            this.exportService.exportToExcel(dataToExport, 'Dealers', this.dealerColumns());
            return;
        }
    }

    /**
     * Closes the bulk delete confirmation modal.
     */
    public closeDeleteConfirmationModal(): void {
        this.showDeleteConfirmationModal.set(false);
        this.selectedDealer.set(null);
    }

    /**
     * Closes the delete success modal and clears the message.
     */
    /**
     * Closes the status modal and clears its content.
     */
    public closeStatusModal(): void {
        this.showStatusModal.set(false);
        this.statusModalMessage.set('');
        this.statusModalTitle.set('');
    }

    /**
     * Confirms the bulk delete action for the currently selected dealer rows.
     */
    public onConfirmDelete(): void {
        const selectedDealer = this.selectedDealer();

        if (!selectedDealer) return;

        this.dealerService
            .deleteSingleDealer(String(selectedDealer.uuid))
            .pipe(
                withLoading(this.isDeletingDealer),
                catchError((err) => {
                    console.error('Error deleting dealer:', err);
                    this.closeDeleteConfirmationModal();
                    this.showStatus(
                        'error',
                        'Deletion Failed',
                        `Failed to delete ${selectedDealer.businessName}. Please try again later.`,
                    );
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.loadInitialData();

                    this.closeDeleteConfirmationModal();
                    this.showStatus(
                        'success',
                        'Successfully Deleted',
                        `${selectedDealer.businessName} has been successfully deleted.`,
                    );
                },
            });

        const selectedDealerIds = this.selectedRows().map((row) => String(row.id));
        const deleteCount = selectedDealerIds.length;

        if (deleteCount === 0) return;

        this.dealerService
            .deleteBulkDealers(selectedDealerIds)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const selectedIdSet = new Set(selectedDealerIds);

                    this.dealerData.update((dealers) =>
                        dealers.filter((dealer) => !selectedIdSet.has(String(dealer.id))),
                    );

                    this.selectedRows.set([]);
                    this.closeDeleteConfirmationModal();
                    this.showStatus(
                        'success',
                        'Successfully Deleted',
                        `${deleteCount} dealer(s) have been successfully deleted.`,
                    );
                },
                error: (error) => {
                    console.error('Failed to delete dealers in bulk.', error);
                    this.closeDeleteConfirmationModal();
                    this.showStatus(
                        'error',
                        'Bulk Delete Failed',
                        'Failed to delete the selected dealers. Please try again later.',
                    );
                },
            });
    }

    /**
     * Shows a status modal with the specified type, title, and message.
     * @param type - The type of modal ('success' | 'error' | 'warning' | 'info')
     * @param title - The title for the modal
     * @param message - The message for the modal
     */
    private showStatus(
        type: 'success' | 'error' | 'warning',
        title: string,
        message: string,
    ): void {
        this.statusModalType.set(type);
        this.statusModalTitle.set(title);
        this.statusModalMessage.set(message);
        this.showStatusModal.set(true);

        // Auto-close success modals after 3 seconds
        if (type === 'success') {
            setTimeout(() => {
                this.closeStatusModal();
            }, 3000);
        }
    }

    /**
     * Projects the local action template into the shared sub-navbar component.
     * Runs after the view initializes and the TemplateRef is available.
     */
    public ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            Promise.resolve().then(() => {
                this.subNavbarService.setContent(this.subNavBarContent()!);
            });
        }
    }

    /**
     * Clears the projected sub-navbar content when the component is destroyed.
     */
    public ngOnDestroy(): void {
        this.subNavbarService.clearContent();
    }

    /**
     * Filter options shown in the "Filter by" dropdown.
     */
    public filterOptions = signal<FilterOption[]>(DEALER_FILTER_OPTIONS);

    /**
     * Updates filter selections and triggers table loading state only on actual selection changes.
     * Ignores expand/collapse events that don't change the selected filters.
     * @param options - The selected filter options
     */
    public onFilterOptionClick(options: FilterOption[]): void {
        const currentFilters = this.selectedFilters();

        // Only trigger loading if the actual selections have changed
        if (JSON.stringify(options) !== JSON.stringify(currentFilters)) {
            this.selectedFilters.set(options);
            this.tableLoading.set(true);
            // Simulate async filtering with a small delay to show skeleton
            requestAnimationFrame(() => {
                this.tableLoading.set(false);
            });
        }
    }

    /**
     * Determines whether a dealer matches a grouped filter.
     */
    private matchesFilterGroup(dealer: ApiDealerTableRow, filterGroup: FilterOption): boolean {
        // Check if this is a license or host range filter (flattened structure from dropdown)
        if (filterGroup.label.includes('licenses')) {
            return this.matchesLicenseRangeLabel(dealer, filterGroup.label);
        }
        if (filterGroup.label.includes('hosts')) {
            return this.matchesHostRangeLabel(dealer, filterGroup.label);
        }

        // Otherwise, use the hierarchical structure approach
        const checkedLabels = filterGroup.children?.map((child) => child.label) ?? [];

        switch (filterGroup.label) {
            case 'Total Count':
                return this.matchesTotalCountFilter(dealer, filterGroup);
            case 'Age':
                return this.matchesAgeFilter(dealer, checkedLabels);
            case 'Unassigned':
                return this.matchesUnassignedFilter(dealer, checkedLabels);
            default:
                return true;
        }
    }

    /**
     * Matches a license range label directly (for flattened filter structure).
     */
    private matchesLicenseRangeLabel(dealer: ApiDealerTableRow, label: string): boolean {
        const licenseCount = dealer.total1;

        if (label === '0-5 licenses') return licenseCount >= 0 && licenseCount <= 5;
        if (label === '6-10 licenses') return licenseCount >= 6 && licenseCount <= 10;
        if (label === '11 and above licenses') return licenseCount >= 11;
        return false;
    }

    /**
     * Matches a host range label directly (for flattened filter structure).
     */
    private matchesHostRangeLabel(dealer: ApiDealerTableRow, label: string): boolean {
        const hostCount = dealer.total2;

        if (label === '0-5 hosts') return hostCount >= 0 && hostCount <= 5;
        if (label === '6-10 hosts') return hostCount >= 6 && hostCount <= 10;
        if (label === '11 and above hosts') return hostCount >= 11;
        return false;
    }

    /**
     * Matches the total count group against licenses and hosts.
     * Checks the nested children of "Licenses" and "Hosts" for specific ranges.
     */
    private matchesTotalCountFilter(dealer: ApiDealerTableRow, filterGroup: FilterOption): boolean {
        const licenseOption = filterGroup.children?.find((child) => child.label === 'Licenses');
        const hostOption = filterGroup.children?.find((child) => child.label === 'Hosts');

        const licenseMatches = licenseOption?.children?.length
            ? this.matchesLicenseRanges(dealer, licenseOption.children)
            : true;

        const hostMatches = hostOption?.children?.length
            ? this.matchesHostRanges(dealer, hostOption.children)
            : true;

        // Return true if either licenses or hosts filters are satisfied
        return licenseMatches && hostMatches;
    }

    /**
     * Matches dealer license count against the selected license range filters.
     */
    private matchesLicenseRanges(
        dealer: ApiDealerTableRow,
        licenseOptions: FilterOption[],
    ): boolean {
        const checkedLabels = licenseOptions
            .filter((option) => option.checked)
            .map((option) => option.label);

        // If no licenses are checked, this filter passes
        if (checkedLabels.length === 0) return true;

        const licenseCount = dealer.total1;

        return checkedLabels.some((label) => {
            if (label === '0-5 licenses') return licenseCount >= 0 && licenseCount <= 5;
            if (label === '6-10 licenses') return licenseCount >= 6 && licenseCount <= 10;
            if (label === '11 and above licenses') return licenseCount >= 11;
            return false;
        });
    }

    /**
     * Matches dealer host count against the selected host range filters.
     */
    private matchesHostRanges(dealer: ApiDealerTableRow, hostOptions: FilterOption[]): boolean {
        const checkedLabels = hostOptions
            .filter((option) => option.checked)
            .map((option) => option.label);

        // If no hosts are checked, this filter passes
        if (checkedLabels.length === 0) return true;

        const hostCount = dealer.total2;

        return checkedLabels.some((label) => {
            if (label === '0-5 hosts') return hostCount >= 0 && hostCount <= 5;
            if (label === '6-10 hosts') return hostCount >= 6 && hostCount <= 10;
            if (label === '11 and above hosts') return hostCount >= 11;
            return false;
        });
    }

    /**
     * Matches the age group using a floor division of age by 30.
     */
    private matchesAgeFilter(dealer: ApiDealerTableRow, checkedLabels: string[]): boolean {
        const ageMonths = Math.floor(dealer.age / 30);

        return checkedLabels.some((label) => {
            if (label === '0-3 months') return ageMonths >= 0 && ageMonths <= 3;
            if (label === '4-9 months') return ageMonths >= 4 && ageMonths <= 9;
            if (label === 'Above 10 months') return ageMonths >= 10;
            return false;
        });
    }

    /**
     * Matches the unassigned group against the unassigned percentage.
     */
    private matchesUnassignedFilter(dealer: ApiDealerTableRow, checkedLabels: string[]): boolean {
        const unassignedPercent = dealer.total1 > 0 ? (dealer.unassigned / dealer.total1) * 100 : 0;

        return checkedLabels.some((label) => {
            if (label === 'Less than 20%') return unassignedPercent < 20;
            if (label === 'Greater than 10%') return unassignedPercent > 10;
            if (label === 'Greater than 20%') return unassignedPercent > 20;
            return false;
        });
    }

    /**
     * Loads the dealer table rows and stats together so the dashboard has one startup state.
     */
    private loadInitialData(): void {
        this.isLoading.set(true);
        this.isPageLoading.set(true);

        forkJoin({
            dealers: this.dealerService.getDealers({ page: 1, limit: this.pageSize }).pipe(
                catchError((error) => {
                    console.error('Failed to load dealer data from API.', error);
                    return EMPTY;
                }),
            ),
            stats: this.dealerService.getDealerStats().pipe(
                map((response) => response.data ?? null),
                catchError((error) => {
                    console.error('Failed to load dealer stats from API.', error);
                    return of(null);
                }),
            ),
        })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                    this.isPageLoading.set(false);
                }),
            )
            .subscribe({
                next: ({ dealers, stats }) => {
                    // Extract pagination metadata from the wrapper
                    this.dealerTotalCount.set(dealers.meta.pagination.totalCount);
                    this.dealerData.set(this.mapDataToUI(dealers.data));
                    this.dealerStats.set(stats);
                },
            });
    }

    /**
     * Maps raw dealer data from the API to the UI table row format.
     * @param data - The array of dealer information from the API
     * @returns An array of mapped table rows
     */
    private mapDataToUI(data: ApiDealerInformation[]): ApiDealerTableRow[] {
        return data.map((dealer: ApiDealerInformation) => ({
            uuid: dealer.user.userId,
            id: dealer.clientInformation.clientInformationId,
            dealerAlias: dealer.clientInformation.alias ?? '',
            businessName: dealer.clientInformation.businessName ?? '',
            contactPerson: (dealer.clientInformation.metadata?.['contactPerson'] ||
                'N/A') as string,
            age: dealer.age,
            licenses: dealer.licenses,
            hosts: dealer.hosts,
            advertisers: dealer.advertisers,
            total1: dealer.licenses.total,
            unassigned: dealer.licenses.unassigned,
            online: dealer.licenses.online,
            offline: dealer.licenses.offline,
            total2: dealer.hosts.total,
            schedule: dealer.hosts.schedule,
            active1: dealer.hosts.active,
            total3: dealer.advertisers.total,
            active2: dealer.advertisers.active,
            status: dealer.status.statusLabel,
            actions: null,
        }));
    }
}
